import { useEffect, useState } from 'react';
import { Input, type InputProps } from '@ets/design-system';

export type MeasureInputProps = {
  valueSuffix?: string;
  avoidEmpty?: boolean;
  min?: number;
  max?: number;
  onChange: (newValue: string) => void;
};

const MeasureField = ({
  valueSuffix,
  avoidEmpty,
  min,
  max,
  onChange,
  ...inputProps
}: Omit<InputProps, 'type' | 'onChange' | 'min' | 'max'> & MeasureInputProps) => {
  //Only display 'valueSuffix' when input isn't focused
  const [focused, setFocused] = useState(false);
  const [lastValidValue, setLastValidValue] = useState<string | null>(null);
  const [invalidValue, setInvalidValue] = useState<string | null>(null);

  useEffect(() => {
    if (!focused) {
      if (avoidEmpty && invalidValue !== null && lastValidValue) {
        onChange(lastValidValue);
      }

      setInvalidValue(null);
    }
  }, [focused]);

  const handleChange: InputProps['onChange'] = (e) => {
    let raw = e.target.value;

    // Normalize input: remove all commas (commas are not supported)
    raw = raw.replace(/,/g, '');

    // Normalize input: allow only one decimal point
    const firstDotIndex = raw.indexOf('.');
    if (firstDotIndex !== -1) {
      raw = raw.slice(0, firstDotIndex + 1) + raw.slice(firstDotIndex + 1).replace(/\./g, '');
    }

    // Validation: single dot alone is not a valid number
    const isOnlyDot = raw === '.';

    // Validation: incomplete decimal (e.g. "12.")
    const isIncompleteDecimal = raw.endsWith('.') && raw.length > 1;

    // Convert to number for range validation
    const numeric = Number(raw);

    if (
      isOnlyDot ||
      (avoidEmpty && raw === '') ||
      Number.isNaN(numeric) ||
      (min !== undefined && numeric < min) ||
      (max !== undefined && numeric > max)
    ) {
      // Keep invalid or intermediate values temporarily
      setInvalidValue(raw);
    } else if (isIncompleteDecimal) {
      // Allow incomplete decimals while typing (e.g. "12.")
      setInvalidValue(raw);
    } else {
      // Commit valid value
      setInvalidValue(null);
      setLastValidValue(raw);
      onChange(raw);
    }
  };

  return (
    <Input
      {...inputProps}
      value={
        !focused && valueSuffix && inputProps.value != null
          ? `${inputProps.value} ${valueSuffix}`
          : (invalidValue ?? inputProps.value)
      }
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={handleChange}
    />
  );
};

export default MeasureField;
